// Slack mrkdwn -> Markdown, for rendering conversation message bodies.
//
// Messages are stored in Slack mrkdwn (that's the flavour Slack needs
// for delivery), but the web Studio renders them through a CommonMark/
// GFM renderer (markstream-vue). Without conversion, raw Slack control
// tokens leak to the reader: `<!channel>`, `<@U123>`, `<#C123|name>`,
// `<https://x|label>`, `<mailto:a@b|label>`, plus `&amp;`/`&lt;`/`&gt;`
// entities. We convert at render time so a single stored body serves
// both surfaces (no dual-authoring by the agent).
//
// This used to flatten every control token to plain text (`@user`,
// `#channel`). It now lifts the *interactive* tokens (user mentions,
// channel references, broadcast pings) into markstream custom-component
// tags - `<mention id="U087">`, `<channel id="C12" name="sales">`,
// `<broadcast kind="here">` - which the renderer maps to chip Vue
// components (avatar + name). Links / mailto / emoji / entities stay
// plain markdown.
//
// ── Extension point ──────────────────────────────────────────────
// `HANDLERS` is an ORDERED registry of token transforms. To support a
// new Slack token type, add a handler object here (and, if it should
// render as a chip rather than plain text, register a custom component
// in `src/main.ts` + add the tag to `SLACK_CUSTOM_TAGS`). Each handler
// runs in order over the non-code spans of the text. Order matters:
// control tokens (real angle brackets) MUST run before entity decode,
// otherwise a decoded `<` from `&lt;` could be mistaken for the start
// of a control token.
//
// STREAMING-SAFE: text arrives in deltas, so each handler's regex only
// matches *complete* tokens (a closing `>` is required); a partial
// trailing `<@U12` is left untouched until the rest arrives, and the
// renderer itself only emits a custom node once the tag is fully formed.

// Full Slack emoji shortcode -> unicode table (generated from
// iamcal/emoji-data, the set Slack itself uses) so any `:name:` the
// agent emits renders the same emoji on the web as it does in Slack.
// Unknown `:name:` are still left verbatim. Emoji in message *content*
// is fine -- the "no emojis" rule is about UI chrome/icons, not the
// user/agent content we mirror from Slack.
import { EMOJI_SHORTCODES as EMOJI } from './emojiShortcodes';
import { githubAlertsToAdmonitions } from './githubAlerts';
import { plainMarkdown } from './text';

// Escape a value that's about to live inside a double-quoted HTML-like
// attribute (`name="…"`). markstream's custom-tag parser reads attrs
// with a `"([^"]*)"` capture, so the only character we must neutralise
// to keep the tag well-formed is the double quote; we also strip the
// angle brackets that would otherwise prematurely close the tag, and
// encode `&` so a literal ampersand in a display name survives the
// later entity-decode pass.
function escAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** One token transform in the ordered conversion pipeline. */
export interface SlackTokenHandler {
  /** Stable name, for debugging / docs. */
  name: string;
  /** Global regex matched against each non-code text span. */
  re: RegExp;
  /** Maps a single match to its replacement string. */
  replace: (m: RegExpMatchArray) => string;
}

// The custom-component tag names this module emits. Mirror this list
// into the `custom-html-tags` prop on every <MarkdownRender> that
// renders Slack bodies, so the parser treats them as streaming custom
// nodes rather than raw HTML. (Exported so the call sites stay in sync
// with the handlers below - one source of truth for the tag set.)
export const SLACK_CUSTOM_TAGS = ['mention', 'channel', 'broadcast'] as const;

// ── The registry ────────────────────────────────────────────────────
// ADD A HANDLER HERE TO SUPPORT A NEW TOKEN TYPE. Control tokens (real
// angle brackets) come first; HTML-entity decode runs last so it can't
// resurrect a `<` that the control-token handlers would then misread.
export const HANDLERS: SlackTokenHandler[] = [
  // User mention: <@U123> or <@U123|name>. -> <mention> chip. The id is
  // the bare Slack user id; the optional inline name is a fallback when
  // the participant directory doesn't resolve it.
  {
    name: 'mention',
    re: /<@([A-Z0-9]+)(?:\|([^>]+))?>/g,
    replace: (m) => {
      const id = m[1];
      const name = m[2];
      return name
        ? `<mention id="${id}" name="${escAttr(name)}"></mention>`
        : `<mention id="${id}"></mention>`;
    },
  },
  // Channel reference: <#C123> or <#C123|name>. -> <channel> chip.
  {
    name: 'channel',
    re: /<#([A-Z0-9]+)(?:\|([^>]+))?>/g,
    replace: (m) => {
      const id = m[1];
      const name = m[2];
      return name
        ? `<channel id="${id}" name="${escAttr(name)}"></channel>`
        : `<channel id="${id}"></channel>`;
    },
  },
  // Broadcast ping: <!channel> / <!here> / <!everyone>. -> emphasized chip.
  {
    name: 'broadcast',
    re: /<!(channel|here|everyone)>/g,
    replace: (m) => `<broadcast kind="${m[1]}"></broadcast>`,
  },
  // User-group (subteam) mention: <!subteam^S123|@group> or bare. We
  // don't have a directory for these yet, so render as a name-only
  // <mention> chip (no id -> initial avatar), keeping the @handle.
  {
    name: 'subteam',
    re: /<!subteam\^[A-Z0-9]+(?:\|@?([^>]+))?>/g,
    replace: (m) => {
      const name = m[1] && m[1].trim() ? m[1].trim() : 'group';
      return `<mention name="${escAttr(name)}"></mention>`;
    },
  },
  // Labelled link: <https://x|label> -> [label](url).
  {
    name: 'link-labeled',
    re: /<(https?:[^|>]+)\|([^>]+)>/g,
    replace: (m) => `[${m[2]}](${m[1]})`,
  },
  // Bare link: <https://x> -> https://x (markstream autolinks it).
  {
    name: 'link-bare',
    re: /<(https?:[^|>]+)>/g,
    replace: (m) => m[1] ?? m[0],
  },
  // Labelled mailto: <mailto:a@b|label> -> label.
  {
    name: 'mailto-labeled',
    re: /<mailto:[^|>]+\|([^>]+)>/g,
    replace: (m) => m[1] ?? m[0],
  },
  // Bare mailto: <mailto:a@b> -> a@b.
  {
    name: 'mailto-bare',
    re: /<mailto:([^>]+)>/g,
    replace: (m) => m[1] ?? m[0],
  },
  // HTML entities. Decode AFTER all control tokens are gone (see header).
  {
    name: 'entity-decode',
    re: /&(lt|gt|amp);/g,
    replace: (m) => (m[1] === 'lt' ? '<' : m[1] === 'gt' ? '>' : '&'),
  },
  // Slack inline markup -> Markdown. Bold `*text*` -> `**text**`
  // (leave already-doubled `**` alone).
  {
    name: 'bold',
    re: /(^|[^*])\*([^*\n]+?)\*(?!\*)/g,
    replace: (m) => `${m[1]}**${m[2]}**`,
  },
  // Italic `_text_` -> `*text*` (avoid mid-word underscores).
  {
    name: 'italic',
    re: /(^|[\s>(,])_([^\s_][^_\n]*?[^\s_])_(?=[\s).,!?]|$)/g,
    replace: (m) => `${m[1]}*${m[2]}*`,
  },
  // Emoji shortcodes -> unicode (known set only; unknown left verbatim).
  {
    name: 'emoji',
    re: /:([a-z0-9_+-]+):/g,
    replace: (m) => (m[1] ? EMOJI[m[1]] : undefined) ?? m[0],
  },
  // NB: currency `$` is handled by escapeCurrencyDollars() below, NOT a handler here -
  // it needs look-ahead (to tell "$5 ..." currency from a "$5x$" math pair), which the
  // per-match handler contract can't express.
];

// Run the ordered registry over one run of non-code text. Each handler
// uses `String.replace` with a function that receives the full match
// array, so handlers can reference capture groups uniformly.
function transformText(s: string): string {
  let out = s;
  for (const h of HANDLERS) {
    // Reset lastIndex defensively (the regexes are module-level globals).
    h.re.lastIndex = 0;
    out = out.replace(h.re, (...args) => {
      // String.replace passes (match, ...groups, offset, string[, groups]).
      // Trim trailing offset/string/namedGroups to leave a clean match array.
      const groups = args.slice(0, -2);
      // Drop a trailing named-groups object if present (none of ours use it).
      if (typeof groups[groups.length - 1] === 'object') groups.pop();
      return h.replace(groups as unknown as RegExpMatchArray);
    });
  }
  return out;
}

// ── One-line plain-text preview (conversation lists) ─────────────────────────
//
// `last_text` is the agent's raw Slack-flavoured message, so a naive
// `plainMarkdown()` leaves Slack tokens (`<mailto:a@b>`, `<url|label>`, `<@U123>`
// mentions, `:emoji:` shortcodes) and inline bullets in the preview. This unwraps
// those tokens to plain text - reusing the SAME `HANDLERS` regexes + `EMOJI` table
// `slackToMarkdown` uses, so there's one source of truth - then strips standard
// markdown via `plainMarkdown` and collapses to a single clean line.
//
// Preview-mode replacements: the renderer path turns mentions/channels/broadcasts
// into chip HTML; here we want plain `@name` / `#name` / `@kind`. Links + mailto +
// entity handlers keep their default replacements (they already emit plain text -
// labelled links become `[label](url)` which `plainMarkdown` then reduces to the
// label). Unknown `:emoji:` are dropped (a preview wants clean prose, not syntax).
const PREVIEW_REPLACE: Record<string, (m: RegExpMatchArray) => string> = {
  mention:   (m) => (m[2] ? `@${m[2]}` : ''),     // <@U|name> -> @name; id-only -> drop
  channel:   (m) => (m[2] ? `#${m[2]}` : ''),     // <#C|name> -> #name; id-only -> drop
  broadcast: (m) => `@${m[1]}`,                   // <!channel> -> @channel
  subteam:   (m) => (m[1] && m[1].trim() ? `@${m[1].trim()}` : '@group'),
  emoji:     (m) => (m[1] ? EMOJI[m[1]] : undefined) ?? '', // known -> unicode, else drop
};

export function messagePreview(input: string | null | undefined): string {
  if (!input) return '';
  // Bound the work: a one-line preview never needs more than the opening of a
  // message, and `last_text` can be multi-KB. Slicing first keeps the per-row
  // regex cost flat; a token clipped past this point sits beyond the visible
  // (CSS-truncated) preview anyway.
  let out = input.slice(0, 600).replace(/```[\s\S]*?```/g, ' '); // drop fenced code
  for (const h of HANDLERS) {
    h.re.lastIndex = 0; // module-level globals carry lastIndex
    const fn = PREVIEW_REPLACE[h.name] ?? h.replace;
    out = out.replace(h.re, (...args) => {
      const groups = args.slice(0, -2);
      if (typeof groups[groups.length - 1] === 'object') groups.pop();
      return fn(groups as unknown as RegExpMatchArray);
    });
  }
  return plainMarkdown(out)         // strip standard markdown + collapse runs
    .replace(/[•◦·▪‣]/g, ' ')        // inline bullet glyphs the agent wrote literally
    .replace(/:::\w*/g, ' ')         // stray admonition fences from the alert rewrite
    .replace(/\s+/g, ' ')
    .trim();
}

// ── Currency `$` vs inline math ──────────────────────────────────────────────
//
// markstream treats a closed `$...$` pair as inline KaTeX math and gives no way to
// disable just that delimiter (see project memory / each app's main.ts). KaTeX stays
// ON because agents emit real math - but they write it as `\(...\)` / `$$...$$` /
// `$x=y$`, none of which open with a bare digit. HQ's content also uses `$` for
// currency ("$85,7 miljarder", "$965B"), and two amounts in one block were being glued
// into one (corrupt) math span. So we escape a *currency* `$` to `\$` before the
// renderer sees it, while leaving a genuine compact `$<digit>...$` math pair alone.

/** Is the text between two `$` a plausible compact math expression (vs currency+prose)? */
function looksLikeInlineMath(expr: string): boolean {
  if (!expr || expr.length > 40) return false;
  if (expr.includes('**')) return false;                        // markdown bold => prose
  const noCommands = expr.replace(/\\[a-zA-Z]+/g, ' ');         // don't count \alpha etc as a word
  if (/[a-zA-Z]{3,}/.test(noCommands)) return false;            // a 3+ letter word => prose
  const hasMathSig = /[\\^_=<>+/(){}|]/.test(expr) || /\\[a-zA-Z]+/.test(expr);
  return hasMathSig || expr.trim().split(/\s+/).length <= 3;    // operator/command or compact run
}

/** Escape a currency `$` (before a digit, not a compact `$math$` pair) to `\$`. */
function escapeCurrencyRun(text: string): string {
  if (text.indexOf('$') === -1) return text;
  // Leading capture skips an already-escaped `\$` and the second `$` of a `$$` pair.
  return text.replace(/(^|[^\\$])\$(?=\d)/g, (full: string, pre: string, offset: number) => {
    const after = text.slice(offset + pre.length + 1);          // text starting at the digit
    const stop = after.search(/[$\n]/);
    if (stop > 0 && after[stop] === '$' && looksLikeInlineMath(after.slice(0, stop))) {
      return full;                                              // genuine math -> leave as-is
    }
    return `${pre}\\$`;                                          // currency -> escape the `$`
  });
}

/**
 * Neutralise currency `$` so the renderer can't mistake it for inline math, while
 * leaving real math intact. Code-span-safe (skips fenced + inline code), so it's safe
 * to call on a raw message body - used by slackToMarkdown and by surfaces that render
 * bodies without it (QuickAsk, HomeView). `\(...\)`, `$$...$$`, and letter-leading
 * `$x$` never match the `(?=\d)` guard, so only currency vs `$<digit>...$` is in play.
 */
export function escapeCurrencyDollars(input: string): string {
  if (!input || input.indexOf('$') === -1) return input;
  const parts: string[] = [];
  const re = /```[\s\S]*?```|`[^`\n]+`/g;
  let cursor = 0;
  let m: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(input))) {
    if (m.index > cursor) parts.push(escapeCurrencyRun(input.slice(cursor, m.index)));
    parts.push(m[0]);                                           // code: leave verbatim
    cursor = m.index + m[0].length;
  }
  if (cursor < input.length) parts.push(escapeCurrencyRun(input.slice(cursor)));
  return parts.join('');
}

export function slackToMarkdown(input: string): string {
  if (!input) return input;

  // Rewrite GitHub-style `> [!NOTE]` alerts into `:::note` admonitions first (the
  // renderer styles the latter, not the former). It skips fenced code blocks itself.
  input = githubAlertsToAdmonitions(input);

  // Neutralise currency `$` (code-span-safe) so it can't open inline math.
  input = escapeCurrencyDollars(input);

  // Tokenise around inline code + fenced code so markup/control-token
  // rewrites never touch the inside of backticks.
  const parts: { kind: 'code' | 'text'; value: string }[] = [];
  const re = /```[\s\S]*?```|`[^`\n]+`/g;
  let cursor = 0;
  let m: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(input))) {
    if (m.index > cursor) parts.push({ kind: 'text', value: input.slice(cursor, m.index) });
    parts.push({ kind: 'code', value: m[0] });
    cursor = m.index + m[0].length;
  }
  if (cursor < input.length) parts.push({ kind: 'text', value: input.slice(cursor) });

  return parts.map(p => (p.kind === 'code' ? p.value : transformText(p.value))).join('');
}
